import React from "react";
import { OrderContact } from "@/components/OrderContact";
import { Link } from "react-router-dom";
import { Mail, Instagram } from "lucide-react";
import { INSTAGRAM_URL } from "@/lib/social";
import { useLang } from "@/lib/lang";

const IG_URL = "https://instagram.com/ozentya_pousse_cheveux";
const TIKTOK_URL = "https://www.tiktok.com/@ozentyapoussecheveu";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.5 3.6 3.5 3.9v2.5c-1.2.1-2.4-.2-3.5-.8v6.2c0 3.2-2.4 5.7-5.6 5.7-3 0-5.4-2.3-5.4-5.3 0-3.1 2.6-5.5 5.9-5.1v2.6c-.4-.1-.8-.2-1.2-.2-1.5 0-2.6 1.2-2.6 2.7 0 1.5 1.1 2.6 2.6 2.6 1.6 0 2.7-1.1 2.7-3V3h3.1z" />
  </svg>
);

const PRO = [
  ["Catalogue professionnel", "Trade catalogue", "/espace-pro", "footer-link-catalogue"],
  ["Coiffure afro", "Afro hair", "/coiffure-afro-professionnelle", "footer-link-afro"],
  ["Coiffure européenne", "European hair", "/coiffure-europeenne-professionnelle", "footer-link-europeenne"],
  ["Barber shop", "Barber shop", "/barber-shop-professionnel", "footer-link-barber"],
  ["Avantages professionnels", "Trade benefits", "/avantages-professionnels", "footer-link-avantages"],
  ["Fournisseur coiffure pro", "Hair trade supplier", "/fournisseur-produits-coiffure-professionnels", "footer-link-fournisseur"],
  ["Grossiste cosmétiques afro", "Afro cosmetics wholesale", "/grossiste-cosmetiques-afro", "footer-link-grossiste"],
  ["Soins capillaires pros", "Professional hair care", "/soins-capillaires-professionnels", "footer-link-soins"],
  ["Instituts & esthétique", "Beauty salons", "/produits-institut-beaute-esthetique", "footer-link-esthetique"],
  ["Livraison France, DOM-TOM, Belgique", "Delivery France, overseas, Belgium", "/livraison-professionnels-france-belgique-dom-tom", "footer-link-livraison-pro"],
  ["Créer mon compte pro", "Open a trade account", "/espace-pro/inscription", "footer-link-inscription"],
  ["Connexion espace pro", "Trade login", "/espace-pro/connexion", "footer-link-connexion"],
  ["Packs particuliers", "Consumer packs", "/packs", "footer-link-packs"],
  ["France, DOM-TOM et Belgique", "France, overseas & Belgium", "/professionnels-beaute-afro", "footer-link-territoires"],
];

const INFO = [
  ["Mentions légales", "Legal notice", "/legal/mentions-legales", true],
  ["CGV", "Terms of sale", "/legal/cgv", true],
  ["Politique de confidentialité", "Privacy policy", "/legal/confidentialite", true],
  ["Politique de cookies", "Cookie policy", "/legal/cookies", true],
  ["Devenir revendeur", "Become a reseller", "/devenir-revendeur", "footer-link-revendeur"],
  ["À propos / Qui sommes-nous", "About us", "/a-propos", "footer-link-a-propos"],
];

const SERVICE = [
  ["Livraison", "Delivery", "/legal/livraison", true],
  ["Retours et remboursements", "Returns & refunds", "/legal/retours", true],
  ["Informations revendeur", "Reseller information", "/legal/revendeur", true],
  ["FAQ", "FAQ", "/packs#faq", null, true],
  ["Suivi de commande", "Order tracking", "/suivi", "footer-link-suivi"],
  ["Blog & conseils", "Blog & tips", "/blog", "footer-link-blog"],
  ["Espace ambassadeur", "Ambassador area", "/ambassadeur", "footer-link-ambassadeur"],
  ["Instagram", "Instagram", "/instagram", "footer-link-instagram-page"],
];

const RESOURCES = [
  ["Conseils cheveux afro", "Afro hair tips", "/conseils-cheveux-afro", "footer-link-conseils-cheveux-afro"],
  ["Complément de revenu", "Extra income", "/complement-de-revenu", "footer-link-complement-de-revenu"],
  ["Revenu complémentaire", "Additional income", "/revenu-complementaire"],
  ["Activité complémentaire", "Side business", "/activite-complementaire"],
  ["Activité à domicile", "Work from home", "/activite-a-domicile"],
  ["Arrondir ses fins de mois", "Make ends meet", "/arrondir-ses-fins-de-mois"],
  ["Augmenter ses revenus", "Increase your income", "/augmenter-ses-revenus"],
  ["Complément de revenu en ligne", "Online extra income", "/complement-de-revenu-en-ligne"],
  ["Sans investissement", "No investment", "/complement-de-revenu-sans-investissement"],
  ["Revenu passif", "Passive income", "/revenu-passif"],
  ["Le soir & le week-end", "Evenings & weekends", "/complement-de-revenu-le-soir-et-week-end"],
  ["Pour salarié", "For employees", "/complement-de-revenu-salarie"],
  ["Pour indépendant", "For freelancers", "/complement-de-revenu-independant"],
];

const linkCls = "block py-1.5 hover:text-white transition-colors";

const LinkList = ({ items, en }) => (
  <ul className="space-y-2.5 text-sm">
    {items.map(([fr, eng, to, tid, raw]) => {
      const testid = tid === true ? `footer-link-${to}` : tid || undefined;
      const label = en ? eng : fr;
      return (
        <li key={to}>
          {raw ? <a href={to} className={linkCls}>{label}</a> : <Link data-testid={testid} to={to} className={linkCls}>{label}</Link>}
        </li>
      );
    })}
  </ul>
);

const Heading = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--oz-gold)] mb-4">{children}</h4>
);

export const Footer = () => {
  const { en, t } = useLang();
  return (
    <footer data-testid="site-footer" className="bg-[var(--oz-dark)] text-white/80">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-3 lg:grid-cols-6">
          <div className="md:col-span-1">
            <div className="font-serif text-2xl text-white mb-3">
              Ozentya<span className="text-[var(--oz-gold)]">.</span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              {t("Des packs beauté à découvrir, présenter et revendre à votre rythme.", "Beauty packs to discover, showcase and resell at your own pace.")}
            </p>
            <OrderContact dark intl={en} title={t("Professionnels : commandes et renseignements", "Professionals: orders and enquiries")} className="mt-5" testid="footer-order-contact" />
            <div className="mt-5 flex items-center gap-3">
              <a href={INSTAGRAM_URL || IG_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram Ozentya Packs Pro"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[var(--oz-gold)] hover:text-white transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok Ozentya"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[var(--oz-gold)] hover:text-white transition-colors">
                <TikTokIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div><Heading>{t("Professionnels", "Professionals")}</Heading><LinkList items={PRO} en={en} /></div>
          <div><Heading>{t("Informations", "Information")}</Heading><LinkList items={INFO} en={en} /></div>
          <div><Heading>{t("Service client", "Customer service")}</Heading><LinkList items={SERVICE} en={en} /></div>
          <div><Heading>{t("Ressources", "Resources")}</Heading><LinkList items={RESOURCES} en={en} /></div>

          <div>
            <Heading>Contact</Heading>
            <a href="mailto:pchaubo@hotmail.fr" data-testid="footer-contact-email" className="inline-flex items-center gap-2 text-sm hover:text-white transition-colors">
              <Mail className="w-4 h-4" /> pchaubo@hotmail.fr
            </a>
            <Link to="/contact" data-testid="footer-contact-button"
              className="mt-4 inline-block text-sm border border-white/20 rounded-full px-5 py-2.5 hover:border-[var(--oz-gold)] hover:text-white transition-colors">
              {t("Nous contacter", "Contact us")}
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Ozentya. {t("Tous droits réservés.", "All rights reserved.")}</p>
          <p>{t("Aucun revenu n'est garanti — les résultats dépendent des ventes réalisées.", "No income is guaranteed — results depend on the sales you make.")}</p>
          <Link to="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 text-white/70 hover:text-[var(--oz-gold)] font-medium transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            {t("Espace admin", "Admin area")}
          </Link>
        </div>
      </div>
    </footer>
  );
};
